/**
 * Área FICHA · ARQUIVOS: as chamadas ao Storage (bucket `materiais`) que as abas da ficha fazem.
 *
 * Um módulo só para o Storage da ficha, e não uma cópia por aba, porque o legado também tem uma
 * função só para cada gesto e as abas a reusam: a lista (`admin.html:6387`), o link assinado
 * (`6345`, `5266`, `5786`), a exclusão (`6367`), o envio (`7762`, `5610`, `5849`) e o download
 * (`9676`). Quem usa: Arquivos, Dossiê, Mensagens, Manual Foto Modelo e E-mail Atendimento.
 *
 * O NOME TEM O PREFIXO `ficha-` para não colidir com a área de Arquivos administrativos
 * (`admin-arquivos`, outra frente), que usa as mesmas chamadas numa pasta fora de cliente.
 */

// Tudo que uma área usa vem do núcleo, e de mais lugar nenhum (ver PADRAO.md).
import {
  BUCKET_MATERIAIS,
  caminhoArquivosCliente,
  clienteDe,
  executar,
  falha,
  mensagemDe,
  sucesso,
  type Escopo,
  type FonteDeDados,
  type Resultado,
} from '../nucleo.js';

/**
 * O que o legado lê de cada item da lista do Storage: só o `name` (`admin.html:6348`).
 * O tipo do SDK não sai do pacote (regra 1 do PADRAO.md), e por isso o formato mora aqui.
 */
export interface ArquivoDaPasta {
  readonly name: string;
}

/** O link assinado que o Storage devolve; o legado lê só `signedUrl` (`admin.html:6354`). */
export interface LinkAssinado {
  readonly signedUrl: string;
}

/**
 * Uma hora de validade no link assinado, o mesmo `3600` das três chamadas do legado
 * (`admin.html:6345`, `5266` e `5786`). O link é gerado a cada carga da aba, e uma hora cobre
 * o tempo de a Lu clicar sem deixar o endereço vivo por dias.
 */
export const VALIDADE_LINK_SEGUNDOS = 3600;

/**
 * As pastas que a lista de uma subpasta percorre, na ordem do legado (`listarPasta`,
 * `admin.html:6405` a `6409`): com lead aberto, a pasta do lead E a pasta achatada antiga; sem
 * lead, só a antiga.
 *
 * POR QUE AS DUAS, e o comentário do legado explica (`6377` a `6380`): arquivos enviados antes da
 * migração de `lead_id` continuam na pasta sem lead, e sem ler as duas eles sumiriam da tela.
 */
export function pastasDaSubpasta(escopo: Escopo, subpasta: string): readonly string[] {
  // A pasta antiga, `<user_id>/<subpasta>`, entra sempre.
  const antiga = `${escopo.userId}/${subpasta}`;
  // Sem lead, a antiga é a única.
  if (escopo.leadId === null) return [antiga];
  // Com lead, a do lead primeiro, como o `[caminhoNovo, caminhoAntigo]` do legado.
  return [caminhoArquivosCliente(escopo, subpasta), antiga];
}

/**
 * Lista uma pasta do bucket.
 * Legado: `admin.html:6387` e `5573`, `sb.storage.from('materiais').list(caminho)`, sem opções,
 * então com o limite e a ordem padrão do Storage, que o legado também usa.
 */
export function listarPasta(fonte: FonteDeDados, caminho: string): Promise<Resultado<readonly ArquivoDaPasta[] | null>> {
  // Sem segundo parâmetro, igual ao legado: acrescentar ordem ou limite mudaria o que aparece.
  return executar<readonly ArquivoDaPasta[] | null>(clienteDe(fonte).storage.from(BUCKET_MATERIAIS).list(caminho));
}

/**
 * Gera o link assinado de um objeto do bucket, válido por uma hora.
 * Legado: `admin.html:6345`, `5266` e `5786`, `createSignedUrl(caminho, 3600)`.
 */
export function gerarLinkAssinado(fonte: FonteDeDados, caminho: string): Promise<Resultado<LinkAssinado | null>> {
  // A validade é a constante acima, a mesma nos três pontos do legado.
  return executar<LinkAssinado | null>(
    clienteDe(fonte).storage.from(BUCKET_MATERIAIS).createSignedUrl(caminho, VALIDADE_LINK_SEGUNDOS),
  );
}

/**
 * Apaga um objeto do bucket.
 * Legado: `admin.html:6367`, `sb.storage.from('materiais').remove([caminho])`.
 */
export function excluirArquivo(fonte: FonteDeDados, caminho: string): Promise<Resultado<unknown>> {
  // Uma lista de um item só, como o legado: a API recebe sempre lista.
  return executar<unknown>(clienteDe(fonte).storage.from(BUCKET_MATERIAIS).remove([caminho]));
}

/** As opções de envio que o legado usa, cada uma num ponto diferente. */
export interface OpcoesDeEnvio {
  // `true` no envio da Lu (`7762`) e no anexo de mensagem (`5849`): o mesmo nome substitui.
  readonly upsert?: boolean;
  // `application/msword` na prévia do dossiê (`5610`), que não passa `upsert`.
  readonly contentType?: string;
}

/**
 * Envia um arquivo para o bucket.
 * Legado: `admin.html:7762` (`{ upsert: true }`), `5849` (`{ upsert: true }`) e `5610`
 * (`{ contentType: 'application/msword' }`).
 *
 * As opções vão exatamente como a tela mandar, sem padrão acrescentado aqui: o dossiê não passa
 * `upsert`, e somar um aqui mudaria o comportamento de nome repetido.
 */
export function enviarArquivo(
  fonte: FonteDeDados,
  caminho: string,
  corpo: Blob,
  opcoes: OpcoesDeEnvio,
): Promise<Resultado<unknown>> {
  // Só as chaves que vieram, para não mandar `upsert: undefined` a quem não pediu.
  const opcoesDoSdk = {
    ...(opcoes.upsert !== undefined ? { upsert: opcoes.upsert } : {}),
    ...(opcoes.contentType !== undefined ? { contentType: opcoes.contentType } : {}),
  };
  return executar<unknown>(clienteDe(fonte).storage.from(BUCKET_MATERIAIS).upload(caminho, corpo, opcoesDoSdk));
}

/**
 * Baixa um objeto do bucket como `Blob`.
 * Legado: `admin.html:9676`, `sb.storage.from('materiais').download(row.arquivo_path)`.
 */
export async function baixarArquivo(fonte: FonteDeDados, caminho: string): Promise<Resultado<Blob | null>> {
  try {
    // O conteúdo volta inteiro; quem decide o que fazer com ele é a tela, como no legado.
    const { data, error } = await clienteDe(fonte).storage.from(BUCKET_MATERIAIS).download(caminho);
    if (!error) return sucesso(data);
    const [mensagem, extra] = await mensagemDoDownload(error);
    return falha(mensagem, extra);
  } catch (lancado) {
    // Nada respondeu: a mensagem do que foi lançado, como o `executar` do núcleo.
    return falha(mensagemDe(lancado));
  }
}

/**
 * A mensagem de uma falha de download, igual à que o legado mostra em "Erro ao baixar: ".
 *
 * POR QUE NÃO BASTA O `executar`: o `storage-js` 2.7.1 deste repositório não lê o corpo da
 * resposta no download (`noResolveJson`) e faz `JSON.stringify(Response)`, então a mensagem chega
 * como "{}". O legado carrega o `supabase-js@2` mais recente do CDN (`admin.html:21`), que mostra a
 * mensagem do corpo: medido no legado com o Storage respondendo 500, o aviso foi
 * "Erro ao baixar: falha simulada em storage", e no novo sem esta leitura, "Erro ao baixar: {}".
 * A ordem dos campos é a do `_getErrorMessage` do próprio `storage-js`.
 */
async function mensagemDoDownload(
  erro: { readonly message: string },
): Promise<[string, { status: number | null }]> {
  // O `Response` original fica em `originalError` (`StorageUnknownError`), com o corpo ainda não lido.
  const original = (erro as { readonly originalError?: unknown }).originalError;
  if (typeof Response === 'undefined' || !(original instanceof Response)) return [erro.message, { status: null }];
  try {
    const corpo = (await original.json()) as Record<string, unknown>;
    // `msg`, `message`, `error_description` e `error`, nesta ordem, como a biblioteca faz no resto.
    const texto = [corpo['msg'], corpo['message'], corpo['error_description'], corpo['error']].find(
      (valor): valor is string => typeof valor === 'string' && valor !== '',
    );
    return [texto ?? erro.message, { status: original.status }];
  } catch {
    // Corpo que não é JSON (uma página de erro do gateway): fica a mensagem da biblioteca.
    return [erro.message, { status: original.status }];
  }
}
